import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppLayout } from '@/components/AppLayout';
import { BAILLEUR_LINKS } from './links';
import { bailleurApi, downloadPdfWithAuth } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { TID } from '@/constants/testIds';
import { ArrowLeft, Download, ReceiptText, Trash2 } from 'lucide-react';
import { Loader } from '@/components/Loader';

const STATUS_META = {
  paid: { label: 'Payé', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  partial: { label: 'Partiel', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  late: { label: 'En retard', cls: 'bg-red-50 text-red-700 border-red-200' },
  pending: { label: 'À venir', cls: 'bg-slate-50 text-slate-700 border-slate-200' },
};

function PaymentDialog({ locataire, echeances, onDone }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const oldestUnpaid = echeances.find((e) => e.statut !== 'paid');
  const [echeanceId, setEcheanceId] = useState(oldestUnpaid?.id || '');
  const [montant, setMontant] = useState(oldestUnpaid ? (oldestUnpaid.montant_du - (oldestUnpaid.montant_paye || 0)).toString() : '');
  const [mode, setMode] = useState('cash');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    const e = echeances.find((x) => x.id === echeanceId);
    if (e) setMontant((e.montant_du - (e.montant_paye || 0)).toString());
  }, [echeanceId, echeances]);

  const submit = async (ev) => {
    ev.preventDefault();
    setLoading(true);
    try {
      await bailleurApi.createPaiement({
        locataire_id: locataire.id,
        echeance_id: echeanceId || null,
        montant: parseFloat(montant),
        mode, reference, note,
      });
      toast.success('Paiement enregistré · Reçu PDF disponible');
      setOpen(false);
      onDone?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  const unpaid = echeances.filter((e) => e.statut !== 'paid');

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={TID.addPayment} className="bg-[#1677FF] hover:bg-[#0E62DB]">
          <ReceiptText className="w-4 h-4 mr-1.5" /> Enregistrer un paiement
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Nouveau paiement</DialogTitle></DialogHeader>
        {unpaid.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">Aucune échéance impayée.</div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Période</Label>
              <Select value={echeanceId} onValueChange={setEcheanceId}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Sélectionner une période" /></SelectTrigger>
                <SelectContent>
                  {unpaid.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.periode_label} — {e.montant_du - (e.montant_paye || 0)} {e.devise} restants
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Montant</Label>
              <Input data-testid={TID.payMontant} type="number" step="0.01" value={montant} onChange={(e) => setMontant(e.target.value)} required className="mt-1.5" />
            </div>
            <div>
              <Label>Mode de paiement</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid={TID.payMode} className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Espèces</SelectItem>
                  <SelectItem value="mobile_money">Mobile Money</SelectItem>
                  <SelectItem value="virement">Virement</SelectItem>
                  <SelectItem value="cheque">Chèque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Référence (optionnel)</Label>
              <Input data-testid={TID.payRef} value={reference} onChange={(e) => setReference(e.target.value)} className="mt-1.5" placeholder="Ex : transaction Airtel Money" />
            </div>
            <div>
              <Label>Note (optionnel)</Label>
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" rows={2} />
            </div>
            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => setOpen(false)}>Annuler</Button>
              <Button data-testid={TID.paySubmit} type="submit" disabled={loading} className="bg-[#1677FF] hover:bg-[#0E62DB]">
                {loading ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function BailleurLocataireDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await bailleurApi.getLocataire(id);
      setData(data);
    } catch (err) {
      console.debug('[BailleurLocataireDetail] load failed:', err?.response?.status);
      nav('/bailleur/locataires');
    }
    finally { setLoading(false); }
  }, [id, nav]);
  useEffect(() => { load(); }, [load]);

  const remove = async () => {
    if (!window.confirm('Supprimer ce locataire ?')) return;
    await bailleurApi.deleteLocataire(id);
    toast.success('Locataire supprimé');
    nav('/bailleur/locataires');
  };

  const openReceipt = async (p) => {
    try {
      await downloadPdfWithAuth(bailleurApi.recuUrl(p.id), `${p.recu_numero}.pdf`);
    } catch { toast.error('Téléchargement impossible'); }
  };

  return (
    <AppLayout links={BAILLEUR_LINKS} testid={TID.bailleurSidebar} title={loading ? '' : `${data.locataire.prenom} ${data.locataire.nom}`}>
      <button onClick={() => nav('/bailleur/locataires')} className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#0D1B2A] -mt-4 mb-4">
        <ArrowLeft className="w-4 h-4" /> Retour aux locataires
      </button>

      {loading || !data ? null : (
        <>
          <div className="lb-card p-6 mb-6">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Locataire</div>
                <div className="mt-1 text-xl font-bold text-[#0D1B2A]">{data.locataire.prenom} {data.locataire.nom}</div>
                <div className="text-sm text-slate-500">{data.locataire.telephone}</div>
                <div className="mt-3 text-sm text-slate-700">{data.locataire.adresse_logement}</div>
                <div className="mt-4 flex items-center gap-4 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Loyer</div>
                    <div className="font-bold text-[#0D1B2A]">{data.locataire.montant_loyer} {data.locataire.devise}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500">Jour d'échéance</div>
                    <div className="font-bold text-[#0D1B2A]">{data.locataire.jour_echeance} du mois</div>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <PaymentDialog locataire={data.locataire} echeances={data.echeances} onDone={load} />
                <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={remove}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>

          <Tabs defaultValue="echeances" className="w-full">
            <TabsList>
              <TabsTrigger value="echeances">Échéances ({data.echeances.length})</TabsTrigger>
              <TabsTrigger value="paiements">Paiements ({data.paiements.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="echeances" className="mt-4">
              <div className="lb-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left">
                        {['Période', 'Échéance', 'Montant dû', 'Payé', 'Statut'].map((h) => (
                          <th key={h} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.echeances.map((e) => {
                        const meta = STATUS_META[e.statut] || STATUS_META.pending;
                        return (
                          <tr key={e.id} className="border-b border-slate-100">
                            <td className="px-4 py-3 font-medium text-[#0D1B2A]">{e.periode_label}</td>
                            <td className="px-4 py-3 text-slate-700">{new Date(e.date_echeance).toLocaleDateString('fr-FR')}</td>
                            <td className="px-4 py-3 font-semibold">{e.montant_du} {e.devise}</td>
                            <td className="px-4 py-3">{e.montant_paye || 0} {e.devise}</td>
                            <td className="px-4 py-3">
                              <Badge className={`${meta.cls} border`}>{meta.label}</Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="paiements" className="mt-4">
              <div className="lb-card overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left">
                        {['N° Reçu', 'Date', 'Période', 'Montant', 'Mode', ''].map((h) => (
                          <th key={h} className="px-4 py-3 text-[11px] font-semibold uppercase tracking-widest text-slate-500">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.paiements.length === 0 ? (
                        <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-500 text-sm">Aucun paiement enregistré.</td></tr>
                      ) : data.paiements.map((p) => (
                        <tr key={p.id} className="border-b border-slate-100">
                          <td className="px-4 py-3 font-mono text-xs text-slate-700">{p.recu_numero}</td>
                          <td className="px-4 py-3 text-slate-700">{new Date(p.date_paiement).toLocaleDateString('fr-FR')}</td>
                          <td className="px-4 py-3 text-slate-700">{p.recu_periode}</td>
                          <td className="px-4 py-3 font-bold text-[#0D1B2A]">{p.montant} {p.devise}</td>
                          <td className="px-4 py-3 text-xs uppercase text-slate-600">{p.mode}</td>
                          <td className="px-4 py-3 text-right">
                            <Button size="sm" variant="outline" onClick={() => openReceipt(p)}>
                              <Download className="w-3.5 h-3.5 mr-1" /> Reçu
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </>
      )}
    </AppLayout>
  );
}
